import React, { useState, useEffect, useCallback } from 'react';
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import HomePage from './pages/HomePage';
import AuthPage from './pages/AuthPage';
import AppPage from './pages/AppPage';
import ProfilePage from './pages/ProfilePage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ResendVerificationPage from './pages/ResendVerificationPage';
import LegalPage from './pages/LegalPage';
import StandaloneLegalPage from './pages/StandaloneLegalPage';
import DashboardPage from './pages/DashboardPage'; // Import DashboardPage
import FeedbackPage from './pages/FeedbackPage'; // Import FeedbackPage
import PaymentSuccessPage from './pages/PaymentSuccessPage'; // Import PaymentSuccessPage
import PaymentCanceledPage from './pages/PaymentCanceledPage'; // Import PaymentCanceledPage
import ForgotPasswordPage from './pages/ForgotPasswordPage'; // Import ForgotPasswordPage
import ResetPasswordPage from './pages/ResetPasswordPage'; // Import ResetPasswordPage
import { auth } from './api';

// App component with routing
const App = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Fetch user profile
  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await auth.getCurrentUser();
      setUser(response.data);
      setIsLoggedIn(true);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      localStorage.removeItem('token');
      setIsLoggedIn(false);
      setLoading(false);
    }
  }, [setUser, setIsLoggedIn, setLoading]);

  // Custom page setter that checks auth when changing to certain pages
  const handlePageChange = useCallback((page, skipHistoryUpdate = false) => {
    // Update browser history with the new page (unless we're responding to a hash change)
    if (!skipHistoryUpdate) {
      // Map page names to hash routes
      const pageToHash = {
        'home': '',
        'auth': '/auth',
        'app': '/app',
        'profile': '/profile',
        'legal': '/legal',
        'feedback': '/feedback',
        'payment-success': '/payment-success',
        'payment-canceled': '/payment-canceled',
        'forgot-password': '/forgot-password',
        'reset-password': '/reset-password',
        'accept-invitation': '/accept-invitation',
        'verify-email': '/verify-email',
        'resend-verification': '/resend-verification',
        'standalone-legal': '?page=legal'
      };

      const hashPath = pageToHash[page];

      // Handle hash updates
      if (hashPath !== undefined) {
        if (hashPath === '') {
          // For home page, remove the hash entirely
          if (window.location.hash !== '') {
            window.history.pushState(null, '', window.location.pathname + window.location.search);
          }
        } else if (hashPath.startsWith('?')) {
          // For query-based routes, use pushState to avoid page reload
          const newUrl = window.location.pathname + hashPath;
          if (window.location.pathname + window.location.search !== newUrl) {
            window.history.pushState(null, '', newUrl);
          }
        } else {
          // For hash-based routes
          const newHash = `#${hashPath}`;
          if (window.location.hash !== newHash) {
            window.location.hash = newHash;
          }
        }
      }
    }

    // If changing to home and auth token exists, check auth status
    if (page === 'home' && localStorage.getItem('token')) {
      console.log('Changing to home page with token - checking auth status');
      fetchUserProfile().then(() => {
        setCurrentPage(page);
      });
    } else {
      setCurrentPage(page);
    }
  }, [fetchUserProfile, setCurrentPage]);

  // Handle initialization and routing on page load
  useEffect(() => {
    console.log("App initialization");

    // Process hash-based routing first
    const initialHash = window.location.hash.substring(1);
    console.log('Initial page load - checking hash:', initialHash);

    // Check if user is logged in
    const authToken = localStorage.getItem('token');

    if (initialHash) {
      const hashPath = initialHash.split('?')[0];
      const hashSegments = hashPath.split('/').filter(segment => segment !== ''); // Remove empty segments
      console.log('Initial hash segments:', hashSegments);

      // Check for specific hash-based routes
      if (hashSegments[0] === 'payment-success') {
        console.log('Initial load - payment success page detected');
        handlePageChange('payment-success');
        setLoading(false);
        return;
      } else if (hashSegments[0] === 'payment-canceled') {
        console.log('Initial load - payment canceled page detected');
        handlePageChange('payment-canceled');
        setLoading(false);
        return;
      } else if (hashSegments[0] === 'auth') {
        console.log('Initial load - auth page detected');

        // If user is already logged in and trying to access auth page, redirect to home
        if (authToken) {
          console.log('User already logged in, clearing auth hash and redirecting to home');
          window.history.replaceState(null, '', window.location.pathname);
          fetchUserProfile().then(() => {
            handlePageChange('home');
          });
          return;
        }

        handlePageChange('auth');
        setLoading(false);
        return;
      }
    }

    if (authToken) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
    
    // Process URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const page = urlParams.get('page');
    const urlToken = urlParams.get('token');
    const verifyEmail = urlParams.get('verify_email') === 'true';
    
    // Process path segments
    const pathSegments = window.location.pathname.split('/');
    const isInvitationPath = pathSegments[1] === 'invitation';
    const pathToken = isInvitationPath ? pathSegments[2] : null;
    
    // Process hash parameters
    const hashPart = window.location.hash.substring(1);
    const hashSegments = hashPart.split('/');
    const isHashInvitationPath = hashSegments.length > 1 && hashSegments[0] === 'accept-invitation';
    
    const hashQuery = hashPart.includes('?') ? hashPart.substring(hashPart.indexOf('?')) : '';
    const hashParams = new URLSearchParams(hashQuery);
    const hashToken = hashParams.get('token');
    
    // Combine tokens from different sources
    const combinedToken = urlToken || pathToken || hashToken;
    
    // Set current page based on URL/path parameters
    if (hashSegments[0] === 'reset-password' && hashToken) {
      // Handle reset-password path with token
      handlePageChange('reset-password');
    } else if (hashSegments[0] === 'forgot-password') {
      // Handle forgot-password path
      handlePageChange('forgot-password');
    } else if (verifyEmail && combinedToken) {
      // Handle email verification
      handlePageChange('verify-email');
    } else if (isHashInvitationPath || isInvitationPath) {
      // Handle invitation paths
      handlePageChange('accept-invitation');
    } else if (page === 'legal') {
      handlePageChange('standalone-legal');
    }
  }, [handlePageChange, fetchUserProfile]); // Add fetchUserProfile as a dependency

  // Listen for hash changes
  useEffect(() => {
    const handleHashChange = () => {
      console.log('Hash change detected!');
      console.log('Current hash:', window.location.hash);

      const hash = window.location.hash.substring(1);
      const hashPath = hash.split('?')[0];
      const hashQuery = hash.includes('?') ? hash.substring(hash.indexOf('?')) : '';

      console.log('Parsed hash path:', hashPath);
      console.log('Parsed hash query:', hashQuery);

      const hashSegments = hashPath.split('/').filter(segment => segment !== ''); // Remove empty segments
      console.log('Hash segments:', hashSegments);

      // Map hash routes to page names
      const hashToPage = {
        '': 'home',
        'auth': 'auth',
        'app': 'app',
        'profile': 'profile',
        'legal': 'legal',
        'feedback': 'feedback',
        'payment-success': 'payment-success',
        'payment-canceled': 'payment-canceled',
        'forgot-password': 'forgot-password',
        'reset-password': 'reset-password',
        'accept-invitation': 'accept-invitation',
        'verify-email': 'verify-email',
        'resend-verification': 'resend-verification'
      };

      // Determine the page from the hash
      const firstSegment = hashSegments[0] || '';
      let targetPage = hashToPage[firstSegment];

      // Handle special cases
      if (firstSegment === 'auth') {
        console.log('Hash-based auth detected');

        // If user is already logged in, don't allow access to auth page
        const authToken = localStorage.getItem('token');
        if (authToken && isLoggedIn) {
          console.log('User already logged in, clearing auth hash and staying on current page');
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }
      }

      // Update the current page (skip history update to avoid infinite loop)
      if (targetPage) {
        console.log(`Navigating to page: ${targetPage}`);
        handlePageChange(targetPage, true);
      } else if (!firstSegment) {
        // Empty hash means home page
        console.log('Empty hash detected, navigating to home');
        handlePageChange('home', true);
      }
    };

    // Initial check on mount
    handleHashChange();

    // Add hash change listener
    window.addEventListener('hashchange', handleHashChange);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [handlePageChange, isLoggedIn]); // Add isLoggedIn as a dependency

  // Login function
  const handleLogin = async (email, password) => {
    try {
      const response = await auth.login(email, password);
      localStorage.setItem('token', response.data.access_token);
      await fetchUserProfile();

      // Clear the #/auth hash from URL to prevent issues on refresh
      if (window.location.hash === '#/auth' || window.location.hash === '#auth') {
        window.history.replaceState(null, '', window.location.pathname);
      }

      handlePageChange('app');
      return { success: true };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        message: error.response?.data?.detail || 'Login failed. Please check your credentials.'
      };
    }
  };

  // Register function
  const handleRegister = async (userData) => {
    try {
      await auth.register(userData);
      // Auto-login after registration
      const response = await auth.login(userData.email, userData.password);
      localStorage.setItem('token', response.data.access_token);
      await fetchUserProfile();

      // Clear the #/auth hash from URL to prevent issues on refresh
      if (window.location.hash === '#/auth' || window.location.hash === '#auth') {
        window.history.replaceState(null, '', window.location.pathname);
      }

      handlePageChange('app');
      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      // Check for specific error messages
      let errorMessage = 'Registration failed. Please try again.';

      if (error.response?.status === 400 && error.response?.data?.detail === "Email already registered") {
        errorMessage = 'This email is already registered. Please use a different email or try to login.';
      } else if (error.response?.data?.detail) {
        errorMessage = error.response?.data?.detail;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    setIsLoggedIn(false);
    handlePageChange('home');
  };

  const renderPage = () => {
    if (loading) {
      return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
    }

    if (currentPage === 'standalone-legal') {
      return <StandaloneLegalPage />;
    }

    if (currentPage === 'verify-email') {
      return <VerifyEmailPage setCurrentPage={handlePageChange} />;
    }

    if (currentPage === 'resend-verification') {
      return <ResendVerificationPage setCurrentPage={handlePageChange} />;
    }

    if (currentPage === 'accept-invitation') {
      return <AcceptInvitationPage 
        handleLogin={handleLogin}
        setCurrentPage={handlePageChange}
      />;
    }

    if (currentPage === 'payment-success') {
      return <PaymentSuccessPage setCurrentPage={handlePageChange} />;
    }

    if (currentPage === 'payment-canceled') {
      return <PaymentCanceledPage setCurrentPage={handlePageChange} />;
    }

    if (currentPage === 'forgot-password') {
      return <ForgotPasswordPage />;
    }

    if (currentPage === 'reset-password') {
      return <ResetPasswordPage />;
    }

    return (
      <>
        <Navbar 
          isLoggedIn={isLoggedIn} 
          handleLogout={handleLogout} 
          setCurrentPage={handlePageChange}
          mobileMenuOpen={mobileMenuOpen}
          setMobileMenuOpen={setMobileMenuOpen}
          user={user}
        />

        <main>
          {currentPage === 'home' && !isLoggedIn && (
            <HomePage 
              setCurrentPage={handlePageChange} 
              isLoggedIn={isLoggedIn} 
            />
          )}
          {currentPage === 'home' && isLoggedIn && (
            <DashboardPage
              user={user}
              redirectToAnalysis={(analysisId) => {
                handlePageChange('app');
                if (analysisId) {
                  // Store the analysis ID to be used by the AppPage
                  localStorage.setItem('selected_analysis_id', analysisId);
                } else {
                  // Clear any previously selected analysis ID when starting a new analysis
                  localStorage.removeItem('selected_analysis_id');
                }
              }}
            />
          )}
          {currentPage === 'feedback' && (
            <FeedbackPage 
              user={user}
              setCurrentPage={handlePageChange}
            />
          )}
          {currentPage === 'auth' && (
            <AuthPage 
              handleLogin={handleLogin} 
              handleRegister={handleRegister}
              setCurrentPage={handlePageChange}
            />
          )}
          {currentPage === 'app' && (
            <AppPage 
              user={user}
              setCurrentPage={handlePageChange}
            />
          )}
          {currentPage === 'profile' && (
            <ProfilePage 
              user={user}
              setCurrentPage={handlePageChange}
              onUserUpdate={(updatedUser) => {
                setUser(updatedUser);
              }}
            />
          )}
          {currentPage === 'legal' && (
            <LegalPage 
              setCurrentPage={handlePageChange} 
            />
          )}
        </main>
        
        <Footer setCurrentPage={handlePageChange} />
      </>
    );
  };

  return renderPage();
};

export default App;